import { Link } from "react-router-dom";
import { Flower, ArrowLeft } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const SECTIONS = [
  {
    id: "overview",
    h: "1. Overview",
    body: [
      "Wingwomen (\"we\", \"us\") is a peer support platform that leverages artificial intelligence to connect patients around their reproductive health. We designed this platform on the belief that no woman should have to choose between finding support and protecting her privacy.",
      "This Privacy Policy explains what we collect, how we use it, how we protect it, and the rights you have over your data. It applies to the website at mywingwomen.com and any related apps or services.",
    ],
  },
  {
    id: "what-we-collect",
    h: "2. What We Collect",
    body: [
      "Account data: email address and a bcrypt-hashed password (for email/password sign-in), or an OAuth identifier (for Google sign-in). Every account also has an anonymous username we generate for you (e.g., BraveWillow42), you never appear under your real name.",
      "Profile & journey data: the conditions, symptoms, treatments, goals, life stage, age range, and optional bio you enter during onboarding. This data powers matching and is never displayed alongside your real name.",
      "Community content: posts, tags, reactions, and messages you send. Posts you mark as anonymous show only your anonymous username.",
      "Optional data: phone number (only if you verify one for SMS alerts), loss dates (only if you privately mark them), and payment metadata (Stripe, if you upgrade to Premium, we do not store your card details).",
      "Automatic data: minimal server logs (IP, user-agent, timestamps) for security and abuse prevention; anonymized analytics on aggregate usage.",
    ],
  },
  {
    id: "how-we-use",
    h: "3. How We Use It",
    body: [
      "To match you with peers whose lived experience mirrors yours (similarity is computed on your profile, never on your identity).",
      "To power the AI Health Navigator conversations you initiate (see Section 6 on third-party AI providers).",
      "To send HIPAA-mindful transactional messages (e.g., \"You have a new anonymous message on Wingwomen\", never containing PHI).",
      "To auto-detect personal identifiers and harassment in posts before they go live (AI Safety Layer).",
      "To improve the platform, in aggregate and de-identified form only.",
      "We do NOT sell your personal data. We do NOT show third-party advertising. We do NOT share individual health data with employers, insurers, or family members.",
    ],
  },
  {
    id: "hipaa",
    h: "4. HIPAA & Security",
    body: [
      "Wingwomen is designed with HIPAA-aligned principles: pseudonymous usernames, encryption in transit (TLS), role-based access, audit logging, and data minimization. Where you are a HIPAA \"covered entity\" or \"business associate\", please contact us before onboarding.",
      "We hash passwords with bcrypt, generate JWT tokens with a rotating secret, and never store your phone verification codes on our servers (we use Twilio Verify for one-time codes).",
      "No system is perfectly secure. If we ever detect unauthorized access to your account, we will notify you promptly and help you take next steps.",
    ],
  },
  {
    id: "cookies",
    h: "5. Cookies & Local Storage",
    body: [
      "We use two categories of browser storage: (a) Essential, to keep you signed in via a JWT stored in localStorage, and to remember your cookie choice and dismissed anniversary banners; (b) Analytics, anonymized aggregate usage patterns.",
      "You can decline analytics on the cookie banner shown on your first visit. Essential storage is required for the platform to work.",
    ],
  },
  {
    id: "third-parties",
    h: "6. Third-Party Providers",
    body: [
      "We use a small number of trusted vendors under strict data-processing agreements. Each sees only the data required to perform its function:",
      "• Anthropic (Claude Sonnet 4.5), powers the AI Health Navigator. Your prompts and profile context are sent to Anthropic when you send a message; responses are streamed back and stored in your session history. Do not include personal identifiers in your prompts.",
      "• ElevenLabs, synthesizes the Navigator's spoken responses when you click \"Listen\". Text-to-speech is generated on demand and not retained.",
      "• Twilio (Verify + Messaging), sends and validates one-time phone codes and transactional SMS. Only your phone number and a generated code are shared with Twilio.",
      "• Emergent (Google Sign-in), proxies Google OAuth. We receive your email, name, and profile picture from Google via Emergent.",
      "• Stripe, handles Premium subscription payments (if you upgrade). We never see your card details.",
      "• MongoDB, our database provider. Data is stored encrypted at rest.",
    ],
  },
  {
    id: "retention",
    h: "7. Data Retention",
    body: [
      "Account and profile data are retained for as long as your account is active.",
      "Disappearing messages are permanently deleted from our servers at the schedule you selected (24 hours, 7 days, or never for permanent).",
      "Loss dates and other private preferences are retained until you remove them.",
      "When you delete your account, all personal data associated with it is deleted within 30 days, except where retention is required by law or for security investigation.",
    ],
  },
  {
    id: "your-rights",
    h: "8. Your Rights",
    body: [
      "You have the right to: (a) access the personal data we hold about you, (b) correct inaccurate data, (c) delete your account and associated data, (d) export a copy of your data, (e) opt out of non-essential communications, and (f) withdraw consent at any time.",
      "To exercise any of these rights, email hi@mywingwomen.com. We respond within 30 days.",
      "If you are in California (CCPA/CPRA), the European Economic Area, or the UK (GDPR), you have additional rights, including the right to lodge a complaint with your local data-protection authority.",
    ],
  },
  {
    id: "children",
    h: "9. Children",
    body: [
      "Wingwomen is for users aged 18 and older (or the age of majority in your jurisdiction). We do not knowingly collect data from minors. If we learn that a minor has created an account, we will delete it.",
    ],
  },
  {
    id: "international",
    h: "10. International Users",
    body: [
      "Wingwomen is operated from the United States. If you access the platform from outside the U.S., your data will be transferred to and processed in the U.S. under safeguards appropriate to the country you're in.",
    ],
  },
  {
    id: "changes",
    h: "11. Changes to This Policy",
    body: [
      "We may update this Privacy Policy over time. Material changes will be announced in-app and by email to the address on your account. Continued use after a change means you accept the updated policy.",
    ],
  },
  {
    id: "ownership",
    h: "12. Ownership & Company Information",
    body: [
      "Wingwomen is a product of Wingwomen, Inc., a venture-backed company founded in 2021. Wingwomen, Inc. received pre-seed investment from StartUp Health in 2022 and the Jumpstart Foundry accelerator in 2023. All references to \"Wingwomen\", \"Wingwomen\", \"we\", or \"us\" in this Privacy Policy refer to Wingwomen, Inc.",
      "The Wingwomen brand, name, logo, tagline, editorial voice, similarity-score matching algorithm, condition hub taxonomy, loss-aware AI system prompts, and all associated product concepts and code are the proprietary intellectual property of Wingwomen, Inc. and its licensors. Wingwomen™, Wingwomen™, and the flower mark are trademarks of Wingwomen, Inc. Third-party names and marks referenced on the Platform (including Anthropic, Claude, ElevenLabs, Stripe, Twilio, and others) are the property of their respective owners and are used for identification only.",
      "For licensing or partnership inquiries, contact hi@mywingwomen.com.",
    ],
  },
  {
    id: "contact",
    h: "13. Contact",
    body: [
      "Questions about your privacy or this policy? Email hi@mywingwomen.com and we'll respond within one business day.",
    ],
  },
];

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy",
    description: "How Wingwomen collects, uses, and protects your reproductive-health journey data, HIPAA-aligned, anonymous by default.",
  });
  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-brand-link" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral-700" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <Link to="/" className="text-sm hover:text-coral-700 flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Privacy</div>
        <h1 className="font-display text-5xl leading-tight" data-testid="privacy-title">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-10 p-5 bg-sage-50 border border-sage-100 rounded-2xl text-sm text-ink/85 leading-relaxed">
          <strong>The short version.</strong> Wingwomen is anonymous by default. We never sell your data,
          never show third-party ads, and never share individual health information with employers,
          insurers, or family members. You can delete your account and all associated data at any time.
        </div>

        <div className="mt-10 grid md:grid-cols-4 gap-x-8">
          <nav className="md:col-span-1 md:sticky md:top-24 h-fit space-y-1.5 mb-8 md:mb-0 text-xs" aria-label="Sections">
            {SECTIONS.map(s => (
              <a key={s.id} href={`#${s.id}`}
                 className="block text-muted-foreground hover:text-coral-700 leading-relaxed">
                {s.h}
              </a>
            ))}
          </nav>
          <div className="md:col-span-3 space-y-10">
            {SECTIONS.map(s => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl leading-snug">{s.h}</h2>
                <div className="mt-3 space-y-3 text-ink/85 leading-relaxed text-[15px]">
                  {s.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter compact />
    </div>
  );
}
